import { CopyButton } from "@arcadeai/design-system/components/ui/atoms/copy-button";
import { Input } from "@arcadeai/design-system/components/ui/atoms/input";
import { Label } from "@arcadeai/design-system/components/ui/atoms/label";
import {
  RadioGroup,
  RadioGroupItem,
} from "@arcadeai/design-system/components/ui/atoms/radio-group";
import { FormItem, FormLabel } from "@arcadeai/design-system/rhf";
import { AlertTriangle } from "lucide-react";
import type React from "react";

export type RedirectUriEditFieldProps = {
  defaultRedirectUri?: string;
  legacyRedirectUri?: string;
  allowLegacy?: boolean;
  useLegacy?: boolean;
  isLoading?: boolean;
  onChangeUseLegacy?: (useLegacy: boolean) => void;
};

export const RedirectUriEditField: React.FC<RedirectUriEditFieldProps> = ({
  defaultRedirectUri,
  legacyRedirectUri,
  allowLegacy = false,
  useLegacy = false,
  isLoading = false,
  onChangeUseLegacy,
}) => {
  const effectiveUri = useLegacy ? legacyRedirectUri : defaultRedirectUri;

  return (
    <FormItem>
      <FormLabel>Redirect URI</FormLabel>

      <div className="relative">
        <Input
          className="pr-10"
          disabled
          readOnly
          value={isLoading ? "Loading..." : effectiveUri || ""}
        />
        <CopyButton
          className="absolute top-1/2 right-2 h-auto -translate-y-1/2 px-2 py-1"
          content={effectiveUri || ""}
          disabled={!effectiveUri || isLoading}
          variant="ghost"
        />
      </div>
      <p className="mt-1 text-muted-foreground text-xs">
        Copy and paste this redirect URI into your OAuth application settings.
      </p>

      {!!allowLegacy && (
        <div className="mb-3">
          <RadioGroup
            className="space-y-3"
            onValueChange={(val) => onChangeUseLegacy?.(val === "legacy")}
            value={useLegacy ? "legacy" : "default"}
          >
            <Label
              className="flex cursor-pointer items-start space-x-3 rounded-md border border-border p-3 transition-colors hover:bg-accent/50"
              htmlFor="redirect-uri-legacy"
            >
              <div className="mt-0.5">
                <RadioGroupItem id="redirect-uri-legacy" value="legacy" />
              </div>
              <div className="space-y-1">
                <div className="font-medium leading-none">
                  Use legacy redirect URI
                </div>
                {!!useLegacy && (
                  <div className="mt-2 flex items-start space-x-2 text-sm">
                    <AlertTriangle className="mt-0.5 h-4 w-4 text-yellow-600 dark:text-yellow-500" />
                    <span className="text-yellow-600 dark:text-yellow-500">
                      This option will be deprecated on September 1, 2025.
                    </span>
                  </div>
                )}
              </div>
            </Label>

            <Label
              className="flex cursor-pointer items-start space-x-3 rounded-md border border-border p-3 transition-colors hover:bg-accent/50"
              htmlFor="redirect-uri-default"
            >
              <div className="mt-0.5">
                <RadioGroupItem id="redirect-uri-default" value="default" />
              </div>
              <div className="space-y-1">
                <div className="font-medium leading-none">
                  Use default redirect URI
                </div>
              </div>
            </Label>
          </RadioGroup>
        </div>
      )}
    </FormItem>
  );
};
